import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { setProfile, setImageViewer, incrementOperation, decrementOperation, setOperationError } from '../Redux/store';
import { supabase } from '../supabase/client';
import ImageCarousel from './ImageCarousel';
import ImageArrayEditor from './ImageArrayEditor';
import ConfirmDialog from './ConfirmDialog';
import { generateImageUuid } from '../utils/imageUrls';

/** Upload a profile image to Supabase storage and return JSONB metadata */
async function uploadProfileImage(userId, file, source = 'upload') {
  const imageUUID = generateImageUuid();
  const path = `${userId}/${imageUUID}`;
  const { error } = await supabase.storage.from('profile-images').upload(path, file, { upsert: true });
  if (error) throw error;
  
  // Return JSONB object with metadata (loading: true during upload)
  return {
    imageUUID,
    source,
    userId,
    bucket: 'profile-images',
    loading: true
  };
}

/** Container for image array editing with database sync and delete confirmation */
export default function ImageArrayEditorContainer({ userId, images, editable = false }) {
  const dispatch = useDispatch();
  const [localImages, setLocalImages] = useState(images || []);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [gridEditMode, setGridEditMode] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);

  // Exit grid edit mode when editable becomes false
  useEffect(() => {
    if (!editable) {
      setGridEditMode(false);
    }
  }, [editable]);

  // Sync localImages with images prop when not editing
  useEffect(() => {
    if (!editable) {
      setLocalImages(images || []);
    }
  }, [images, editable]);

  const handleDelete = async (index) => {
    setDeleteIndex(index);
  };

  const confirmDelete = async () => {
    if (deleteIndex === null) return;

    // Increment global operation counter
    dispatch(incrementOperation('profile'));

    // Optimistic Redux update
    const oldImages = [...localImages];
    const updated = localImages.filter((_, i) => i !== deleteIndex);
    setLocalImages(updated);

    try {
      const { data, error } = await supabase
        .from('profiles')
        .update({ images: updated })
        .eq('id', userId)
        .select()
        .maybeSingle();

      if (error) {
        // Set error message
        const errorMessage = error.message?.toLowerCase().includes('network') || error.message?.toLowerCase().includes('fetch')
          ? 'Error: No internet connection'
          : 'Error: Failed to save';
        dispatch(setOperationError({ type: 'profile', error: errorMessage }));
        // Revert on error
        setLocalImages(oldImages);
      } else if (data) {
        dispatch(setProfile(data));
      }
    } catch (e) {
      // Set error message
      const errorMessage = e.message?.toLowerCase().includes('network') || e.message?.toLowerCase().includes('fetch')
        ? 'Error: No internet connection'
        : 'Error: Failed to save';
      dispatch(setOperationError({ type: 'profile', error: errorMessage }));
      // Revert on error
      setLocalImages(oldImages);
    } finally {
      // Decrement global operation counter
      dispatch(decrementOperation('profile'));
      setDeleteIndex(null);
    }
  };

  const handleReorder = async (newImages) => {
    // Increment global operation counter
    dispatch(incrementOperation('profile'));

    // Optimistic Redux update
    const oldImages = [...localImages];
    setLocalImages(newImages);

    try {
      const { data, error } = await supabase
        .from('profiles')
        .update({ images: newImages })
        .eq('id', userId)
        .select()
        .maybeSingle();

      if (error) {
        // Set error message
        const errorMessage = error.message?.toLowerCase().includes('network') || error.message?.toLowerCase().includes('fetch')
          ? 'Error: No internet connection'
          : 'Error: Failed to save';
        dispatch(setOperationError({ type: 'profile', error: errorMessage }));
        // Revert on error
        setLocalImages(oldImages);
      } else if (data) {
        dispatch(setProfile(data));
      }
    } catch (e) {
      // Set error message
      const errorMessage = e.message?.toLowerCase().includes('network') || e.message?.toLowerCase().includes('fetch')
        ? 'Error: No internet connection'
        : 'Error: Failed to save';
      dispatch(setOperationError({ type: 'profile', error: errorMessage }));
      // Revert on error
      setLocalImages(oldImages);
    } finally {
      // Decrement global operation counter
      dispatch(decrementOperation('profile'));
    }
  };

  const handleAdd = async (fileOrMetadata) => {
    setLoading(true);
    setUploading(true);

    // Increment global operation counter
    dispatch(incrementOperation('profile'));

    try {
      let imageData;
      
      // Check if it's already metadata (from ProfileAttachmentMenu) or a file
      if (fileOrMetadata && typeof fileOrMetadata === 'object' && fileOrMetadata.imageUUID) {
        // Already uploaded metadata - use directly
        imageData = { ...fileOrMetadata, loading: false };
      } else {
        // File - upload it
        imageData = await uploadProfileImage(userId, fileOrMetadata, 'upload');
      }
      
      const updated = [...localImages, imageData];

      // Optimistic Redux update
      const oldImages = [...localImages];
      setLocalImages(updated);
      setCarouselIndex(updated.length - 1);

      // Update with loading: false after DB save
      const finalImages = updated.map(img => ({ ...img, loading: false }));

      const { data, error } = await supabase
        .from('profiles')
        .update({ images: finalImages })
        .eq('id', userId)
        .select()
        .maybeSingle();

      if (error) {
        console.error('[ImageArrayEditorContainer] Supabase error:', error);
        // Set error message
        const errorMessage = error.message?.toLowerCase().includes('network') || error.message?.toLowerCase().includes('fetch')
          ? 'Error: No internet connection'
          : 'Error: Failed to save';
        dispatch(setOperationError({ type: 'profile', error: errorMessage }));
        // Revert on error
        setLocalImages(oldImages);
      } else if (data) {
        setLocalImages(data.images);
        dispatch(setProfile(data));
      }
    } catch (e) {
      console.error('[ImageArrayEditorContainer] handleAdd catch error:', e);
      // Set error message
      const errorMessage = e.message?.toLowerCase().includes('network') || e.message?.toLowerCase().includes('fetch')
        ? 'Error: No internet connection'
          : 'Error: Failed to save';
      dispatch(setOperationError({ type: 'profile', error: errorMessage }));
      // Revert on error
      setLocalImages(localImages);
    } finally {
      // Decrement global operation counter
      dispatch(decrementOperation('profile'));
      setLoading(false);
      setUploading(false);
    }
  };

  const handleReplace = async (index, fileOrMetadata) => {
    setLoading(true);
    setUploading(true);

    // Increment global operation counter
    dispatch(incrementOperation('profile'));

    try {
      let imageData;
      
      // Check if it's already metadata (from ProfileAttachmentMenu) or a file
      if (fileOrMetadata && typeof fileOrMetadata === 'object' && fileOrMetadata.imageUUID) {
        // Already uploaded metadata - use directly
        imageData = { ...fileOrMetadata, loading: false };
      } else {
        // File - upload it
        imageData = await uploadProfileImage(userId, fileOrMetadata, 'upload');
      }
      
      const updated = [...localImages];
      updated[index] = imageData;

      // Optimistic Redux update
      const oldImages = [...localImages];
      setLocalImages(updated);

      // Update with loading: false after DB save
      const finalImages = updated.map(img => ({ ...img, loading: false }));

      const { data, error } = await supabase
        .from('profiles')
        .update({ images: finalImages })
        .eq('id', userId)
        .select()
        .maybeSingle();

      if (error) {
        console.error('[ImageArrayEditorContainer] Supabase error:', error);
        // Set error message
        const errorMessage = error.message?.toLowerCase().includes('network') || error.message?.toLowerCase().includes('fetch')
          ? 'Error: No internet connection'
          : 'Error: Failed to save';
        dispatch(setOperationError({ type: 'profile', error: errorMessage }));
        // Revert on error
        setLocalImages(oldImages);
      } else if (data) {
        setLocalImages(data.images);
        dispatch(setProfile(data));
      }
    } catch (e) {
      console.error('[ImageArrayEditorContainer] handleReplace catch error:', e);
      // Set error message
      const errorMessage = e.message?.toLowerCase().includes('network') || e.message?.toLowerCase().includes('fetch')
        ? 'Error: No internet connection'
          : 'Error: Failed to save';
      dispatch(setOperationError({ type: 'profile', error: errorMessage }));
      // Revert on error
      setLocalImages(localImages);
    } finally {
      // Decrement global operation counter
      dispatch(decrementOperation('profile'));
      setLoading(false);
      setUploading(false);
    }
  };

  const handleImageClick = (index) => {
    // Pass JSONB objects directly to setImageViewer (no wrapping as {url})
    dispatch(setImageViewer({ images: localImages, initialIndex: index }));
  };

  const handleEnterGridEdit = () => {
    setGridEditMode(true);
  };

  const handleExitGridEdit = () => {
    setGridEditMode(false);
  };

  return (
    <>
      {/* Uploading indicator - non-obstructive at top center */}
      {uploading && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 rounded-full bg-background/90 px-4 py-2 shadow-lg backdrop-blur-sm">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <span className="text-xs font-medium text-foreground">Uploading...</span>
        </div>
      )}

      {gridEditMode ? (
        /* Image grid editor */
        <div className="relative">
          {/* Loading indicator - local to grid editor */}
          {loading && (
            <div className="absolute inset-0 z-30 flex items-center justify-center bg-background/50 backdrop-blur-sm">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          )}
          <ImageArrayEditor
            images={localImages}
            onDelete={handleDelete}
            onReorder={handleReorder}
            onAdd={handleAdd}
            onDone={handleExitGridEdit}
            onImageClick={handleImageClick}
          />
        </div>
      ) : (
        /* Image carousel */
        <ImageCarousel
          images={localImages}
          editable={editable}
          onAdd={handleAdd}
          onRemove={handleDelete}
          onReplace={handleReplace}
          onEnterGridEdit={editable ? handleEnterGridEdit : undefined}
          onImageClick={handleImageClick}
          initialIndex={carouselIndex}
        />
      )}

      {/* Delete confirmation dialog */}
      {deleteIndex !== null && (
        <ConfirmDialog
          message="Are you sure you want to delete this photo?"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          destructive
          onConfirm={confirmDelete}
          onCancel={() => setDeleteIndex(null)}
        />
      )}
    </>
  );
}
